import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateProjectInput, ProjectDetail, ProjectFlagsInput, ProjectSummary, Referential, SyncDiff, UpdateProjectInput,
} from './types';

const API = '/api/plan-chaleur-v2';
const KEY = ['plan2cost', 'projet-chaleur-v2'] as const;

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init?.body ? { ...init, headers: { 'Content-Type': 'application/json' } } : init);
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  return res.json() as Promise<T>;
}

/** Référentiel UrbanMap actuel (bâtiments, communes) + valeurs par défaut (démonstration). */
export function useReferential() {
  return useQuery<Referential>({
    queryKey: [...KEY, 'referential'],
    queryFn: () => request(`${API}/referential`),
    staleTime: Infinity,
  });
}

export function useProjects(empty = false) {
  return useQuery<ProjectSummary[]>({
    queryKey: [...KEY, 'projects', empty],
    queryFn: () => request(`${API}/projects${empty ? '?empty=1' : ''}`),
  });
}

export function useProject(id: string | null) {
  return useQuery<ProjectDetail>({
    queryKey: [...KEY, 'project', id],
    queryFn: () => request(`${API}/projects/${id}`),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation<ProjectSummary, Error, CreateProjectInput>({
    mutationFn: (input) => request(`${API}/projects`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'projects'] }),
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation<ProjectDetail, Error, UpdateProjectInput>({
    mutationFn: (patch) => request(`${API}/projects/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: (data) => {
      qc.setQueryData([...KEY, 'project', id], data);
      qc.invalidateQueries({ queryKey: [...KEY, 'projects'] });
    },
  });
}

export function useSyncDiff(id: string, enabled: boolean) {
  return useQuery<SyncDiff>({
    queryKey: [...KEY, 'sync', id],
    queryFn: () => request(`${API}/projects/${id}/sync`),
    enabled,
    staleTime: 0,
  });
}

export function useApplySync(id: string) {
  const qc = useQueryClient();
  return useMutation<ProjectDetail, Error, void>({
    mutationFn: () => request(`${API}/projects/${id}/sync`, { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData([...KEY, 'project', id], data);
      qc.invalidateQueries({ queryKey: [...KEY, 'projects'] });
      qc.invalidateQueries({ queryKey: [...KEY, 'sync', id] });
    },
  });
}

/** Accueil : favori et archivage d'un projet. */
export function useProjectFlags() {
  const qc = useQueryClient();
  return useMutation<ProjectSummary, Error, { id: string; flags: ProjectFlagsInput }>({
    mutationFn: ({ id, flags }) => request(`${API}/projects/${id}/flags`, { method: 'PATCH', body: JSON.stringify(flags) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'projects'] }),
  });
}

/** Accueil : duplication d'un projet (la copie rejoint la famille du projet d'origine). */
export function useDuplicateProject() {
  const qc = useQueryClient();
  return useMutation<ProjectSummary, Error, string>({
    mutationFn: (id) => request(`${API}/projects/${id}/duplicate`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'projects'] }),
  });
}

/** Accueil : suppression définitive d'un projet et de ses copies. */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const res = await fetch(`${API}/projects/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'projects'] }),
  });
}
